import { useEffect, useState } from "react";
import { fetchDashboard, mockDashboard, type DashboardResponse } from "./lib/api";
import { formatTime } from "./lib/format";
import { TabBar } from "./components/TabBar";
import { CompanyView } from "./components/CompanyView";

export default function App() {
  const [data, setData] = useState<DashboardResponse | null>(null);
  const [activeCompanyId, setActiveCompanyId] = useState<number>(1);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [usingMock, setUsingMock] = useState(false);

  useEffect(() => {
    let cancelled = false;
    fetchDashboard(30)
      .then((d) => {
        if (cancelled) return;
        setData(d);
        if (d.companies.length > 0) setActiveCompanyId(d.companies[0].id);
        setLoading(false);
      })
      .catch((err) => {
        if (cancelled) return;
        console.warn("API unavailable, using mock data:", err);
        const mock = mockDashboard();
        setData(mock);
        setActiveCompanyId(mock.companies[0].id);
        setUsingMock(true);
        setError(err.message);
        setLoading(false);
      });
    return () => { cancelled = true; };
  }, []);

  if (loading) return <LoadingScreen />;
  if (!data) return <ErrorScreen message={error ?? "No data"} />;

  const activeCompany =
    data.companies.find((c) => c.id === activeCompanyId) ?? data.companies[0];

  return (
    <div className="min-h-screen flex flex-col bg-bg">
      <Header generatedAt={data.generatedAt} usingMock={usingMock} />

      <main className="flex-1 max-w-container w-full mx-auto px-8 py-12">
        <TabBar
          companies={data.companies}
          activeId={activeCompanyId}
          onSelect={setActiveCompanyId}
        />

        {activeCompany && <CompanyView company={activeCompany} />}
      </main>

      <Footer generatedAt={data.generatedAt} rangeDays={data.rangeDays} />
    </div>
  );
}

function Header({ generatedAt, usingMock }: { generatedAt: number; usingMock: boolean }) {
  return (
    <header className="border-b border-border bg-bg sticky top-0 z-10">
      <div className="max-w-container mx-auto px-8 py-6 flex items-end justify-between">
        <div>
          <div className="flex items-baseline gap-4">
            <h1 className="font-display text-3xl text-ink tracking-tightest font-medium">
              望楼
            </h1>
            <span className="font-mono text-xs text-faint tracking-wider">
              uphash.info
            </span>
          </div>
          <div className="text-xs text-muted mt-1 italic">
            a watchtower for the things you've built
          </div>
        </div>

        <div className="text-right">
          {usingMock && (
            <div className="text-[10px] tracking-editorial text-negative uppercase mb-1 font-medium">
              ⚠ MOCK DATA
            </div>
          )}
          <div className="text-[10px] tracking-editorial text-faint uppercase mb-0.5 font-medium">
            Last updated
          </div>
          <div className="font-mono text-xs text-muted tnum">
            {formatTime(generatedAt)}
          </div>
        </div>
      </div>
    </header>
  );
}

function Footer({ generatedAt, rangeDays }: { generatedAt: number; rangeDays: number }) {
  return (
    <footer className="border-t border-border mt-16 bg-canvas">
      <div className="max-w-container mx-auto px-8 py-6 flex justify-between text-xs text-faint font-mono">
        <span>range: {rangeDays}d</span>
        <span>generated {new Date(generatedAt).toISOString()}</span>
      </div>
    </footer>
  );
}

function LoadingScreen() {
  return (
    <div className="min-h-screen flex items-center justify-center bg-bg">
      <div className="font-display text-xl text-muted italic">観測中…</div>
    </div>
  );
}

function ErrorScreen({ message }: { message: string }) {
  return (
    <div className="min-h-screen flex flex-col items-center justify-center gap-3 bg-bg">
      <div className="font-display text-2xl text-negative">観測失敗</div>
      <div className="font-mono text-xs text-muted max-w-md text-center">{message}</div>
    </div>
  );
}
